import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, ArrowUpDown, Download } from 'lucide-react';
import { TableOutput, ColumnDefinition } from '../types';

interface TableViewerProps {
    data: TableOutput;
}

type SortConfig = {
    column: string;
    direction: 'asc' | 'desc';
} | null;

export const TableViewer: React.FC<TableViewerProps> = ({ data }) => {
    const [sortConfig, setSortConfig] = useState<SortConfig>(null);

    const sortedRows = useMemo(() => {
        if (!sortConfig) return data.rows;

        const sorted = [...data.rows].sort((a, b) => {
            const aVal = a[sortConfig.column];
            const bVal = b[sortConfig.column];

            if (aVal === null || aVal === undefined) return 1;
            if (bVal === null || bVal === undefined) return -1;

            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }

            const aStr = String(aVal).toLowerCase();
            const bStr = String(bVal).toLowerCase();

            if (sortConfig.direction === 'asc') {
                return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
            } else {
                return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
            }
        });

        return sorted;
    }, [data.rows, sortConfig]);

    const handleSort = (column: ColumnDefinition) => {
        if (!column.sortable) return;

        setSortConfig(current => {
            if (!current || current.column !== column.name) {
                return { column: column.name, direction: 'asc' };
            }
            if (current.direction === 'asc') {
                return { column: column.name, direction: 'desc' };
            }
            return null;
        });
    };

    const formatValue = (value: any, column: ColumnDefinition): string => {
        if (value === null || value === undefined) return '-';

        if (column.dataType === 'number' && typeof value === 'number') {
            if (column.format) {
                const decimals = parseInt(column.format.replace('N', ''));
                return value.toFixed(decimals);
            }
            return value.toLocaleString('nb-NO');
        }

        if (column.dataType === 'date' && value) {
            try {
                const date = new Date(value);
                if (column.format) {
                    return date.toLocaleDateString('nb-NO');
                }
                return date.toLocaleString('nb-NO');
            } catch {
                return String(value);
            }
        }

        return String(value);
    };

    const handleExportCSV = () => {
        const headers = data.columns.map(col => col.displayName).join(';');
        const rows = sortedRows.map(row =>
            data.columns.map(col => {
                const value = row[col.name];
                const formatted = formatValue(value, col);
                return formatted.includes(';') || formatted.includes('\n')
                    ? `"${formatted.replace(/"/g, '""')}"`
                    : formatted;
            }).join(';')
        );

        const csv = [headers, ...rows].join('\n');
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${data.title || 'table'}-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
    };

    return (
        <div className="w-full">
            <div className="flex items-center justify-between mb-4">
                <div>
                    {data.title && (
                        <h3 className="text-lg font-semibold text-gray-800">{data.title}</h3>
                    )}
                    {data.description && (
                        <p className="text-sm text-gray-600 mt-1">{data.description}</p>
                    )}
                </div>
                <button
                    onClick={handleExportCSV}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                >
                    <Download size={16} />
                    <span>Eksporter CSV</span>
                </button>
            </div>

            <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-100 sticky top-0">
                            <tr>
                                {data.columns.map((column, idx) => (
                                    <th
                                        key={idx}
                                        onClick={() => handleSort(column)}
                                        className={`px-4 py-3 text-left text-sm font-semibold text-gray-700 border-b ${
                                            column.sortable ? 'cursor-pointer hover:bg-gray-200' : ''
                                        }`}
                                        style={{ width: column.width || 'auto' }}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span>{column.displayName}</span>
                                            {column.sortable && (
                                                <span className="text-gray-400">
                                                    {sortConfig?.column === column.name ? (
                                                        sortConfig.direction === 'asc' ? (
                                                            <ChevronUp size={16} />
                                                        ) : (
                                                            <ChevronDown size={16} />
                                                        )
                                                    ) : (
                                                        <ArrowUpDown size={14} className="opacity-50" />
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.map((row, rowIdx) => (
                                <tr key={rowIdx} className="border-b hover:bg-gray-50">
                                    {data.columns.map((column, colIdx) => (
                                        <td
                                            key={colIdx}
                                            className="px-4 py-3 text-sm text-gray-700"
                                        >
                                            {formatValue(row[column.name], column)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {sortedRows.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                    <p>Ingen rader å vise</p>
                </div>
            )}

            <div className="mt-4 text-sm text-gray-600">
                Viser {sortedRows.length} rad{sortedRows.length !== 1 ? 'er' : ''}
            </div>
        </div>
    );
};
